import type { TeactNode } from '../lib/teact/teact';

import type { LangCode, LangPack, LangString } from '../global/types';

import { IS_PACKAGED_ELECTRON, LANG_CACHE_NAME, LANG_LIST, NATIVE_BIOMETRICS_PROMPT_KEY } from '../config';
import renderText from '../global/helpers/renderText';
// @ts-ignore this file is autogenerated
import defaultLangPackJson from '../i18n/en.json';
import * as cacheApi from './cacheApi';
import { createCallbackManager } from './callbacks';
import { formatNumber } from './formatNumber';
import { setNativeDigitsLang } from './nativeDigits';
import { escapeStringRegexp } from './regex';
import { DEFAULT_LANG_CODE, IS_ELECTRON } from './windowEnvironment';

const defaultLangPack: LangPack = defaultLangPackJson;

export interface LangFn {
  (key: string): string;

  (key: string, value: any, format?: 'i', pluralValue?: number): string | TeactNode[];

  isRtl?: boolean;
  code?: LangCode;
  langName?: string;
}

const {
  addCallback,
  removeCallback,
  runCallbacks,
} = createCallbackManager();

export { addCallback, removeCallback };

const SUBSTITUTION_REGEX = /%(?:(\d+)\$)?[sdf@](?![a-zA-Z0-9_])/g;
const NAMED_SUBSTITUTION_REGEX = /%([a-zA-Z_][a-zA-Z0-9_]*)%/g;
const PLURAL_VALUE_OPTIONS = ['zeroValue', 'oneValue', 'twoValue', 'fewValue', 'manyValue', 'otherValue'] as const;
const PLURAL_OPTIONS = ['value', ...PLURAL_VALUE_OPTIONS] as const;
// Some rules edited from https://github.com/eemeli/make-plural/blob/master/packages/plurals/cardinals.js
// ar - zeroValue, oneValue, twoValue, fewValue, manyValue, otherValue
// de - zeroValue, oneValue, otherValue
// en - zeroValue, oneValue, otherValue
// es - zeroValue, oneValue, otherValue
// fa - zeroValue, oneValue, otherValue
// pl - zeroValue, oneValue, fewValue, manyValue
// ru - zeroValue, oneValue, fewValue, manyValue
// th - zeroValue, otherValue
// tr - zeroValue, oneValue, otherValue
// uk - zeroValue, oneValue, fewValue, manyValue
// vi - zeroValue, otherValue
// zh-Hans - zeroValue, otherValue
// zh-Hant - zeroValue, otherValue
const PLURAL_RULES = {
  /* eslint-disable @stylistic/max-len */
  ar: (n: number) => (n === 0 ? 1 : (n === 1 ? 2 : (n === 2 ? 3 : (n % 100 >= 3 && n % 100 <= 10 ? 4 : (n % 100 >= 11 && n % 100 <= 99 ? 5 : 6))))),
  de: (n: number) => (n === 0 ? 1 : (n !== 1 ? 6 : 2)),
  en: (n: number) => (n === 0 ? 1 : (n !== 1 ? 6 : 2)),
  es: (n: number) => (n === 0 ? 1 : (n !== 1 ? 6 : 2)),
  fa: (n: number) => (n === 0 ? 1 : (n === 1 ? 2 : 6)),
  pl: (n: number) => (n === 0 ? 1 : (n === 1 ? 2 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 4 : 5)),
  ru: (n: number) => (n === 0 ? 1 : (n % 10 === 1 && n % 100 !== 11 ? 2 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 4 : 5)),
  th: (n: number) => (n === 0 ? 1 : 6),
  tr: (n: number) => (n === 0 ? 1 : (n > 1 ? 6 : 2)),
  uk: (n: number) => (n === 0 ? 1 : (n % 10 === 1 && n % 100 !== 11 ? 2 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 4 : 5)),
  vi: (n: number) => (n === 0 ? 1 : 6),
  'zh-Hans': (n: number) => (n === 0 ? 1 : 6),
  'zh-Hant': (n: number) => (n === 0 ? 1 : 6),
  /* eslint-enable @stylistic/max-len */
};
const cache = new Map<string, string>();
let langPack: LangPack | undefined;
let currentLangCode: string | undefined;

function createLangFn() {
  return ((key: string, value?: any, format?: 'i', pluralValue?: number) => {
    if (value !== undefined && (typeof value !== 'object' || Array.isArray(value))) {
      const cacheValue = Array.isArray(value) ? JSON.stringify(value) : value;
      const cached = cache.get(`${key}_${cacheValue}_${format}${pluralValue ? `_${pluralValue}` : ''}`);
      if (cached) {
        return cached;
      }
    }

    if (!langPack && !defaultLangPack) {
      return key;
    }

    const langString = (langPack?.[key]) || (defaultLangPack?.[key]) || key;

    return processTranslation(langString, key, value, format, pluralValue);
  }) as LangFn;
}

export let getTranslation: LangFn = createLangFn();

export async function setLanguage(langCode: LangCode, callback?: NoneToVoidFunction) {
  const langInfo = LANG_LIST?.find((l) => l.langCode === langCode);

  // Apply direction synchronously (the `rtl` flag comes from LANG_LIST, not the langpack) so the
  // first render is already RTL/LTR-correct and does not flash the opposite direction
  document.documentElement.lang = langCode;
  document.documentElement.dir = langInfo?.rtl ? 'rtl' : 'ltr';
  getTranslation.isRtl = Boolean(langInfo?.rtl);
  setNativeDigitsLang(langCode.replace('-raw', ''));

  if (langPack && langCode === currentLangCode) {
    if (callback) {
      callback();
    }

    return;
  }

  let newLangPack = await cacheApi.fetch(LANG_CACHE_NAME, langCode);

  if (!newLangPack) {
    newLangPack = await fetchRemote(langCode);
    if (!newLangPack) {
      return;
    }
  }

  cache.clear();

  currentLangCode = langCode;
  langPack = newLangPack;

  getTranslation = createLangFn();
  getTranslation.isRtl = Boolean(langInfo?.rtl);
  getTranslation.code = langCode.replace('-raw', '') as LangCode;
  getTranslation.langName = langInfo?.nativeName;

  if (callback) {
    callback();
  }

  runCallbacks();

  if (IS_ELECTRON) {
    void window.electron?.setBiometricPrompt?.(getTranslation(NATIVE_BIOMETRICS_PROMPT_KEY));
  }
}

function getLangCacheVersion() {
  return Number((LANG_CACHE_NAME.match(/\d+$/) || [0])[0]);
}

export function clearPreviousLangpacks() {
  const langCachePrefix = LANG_CACHE_NAME.replace(/\d+$/, '');
  const langCacheVersion = getLangCacheVersion();
  for (let i = 0; i < langCacheVersion; i++) {
    void cacheApi.clear(`${langCachePrefix}${i === 0 ? '' : i}`);
  }
}

async function fetchRemote(langCode: string): Promise<LangPack | undefined> {
  if (langCode === DEFAULT_LANG_CODE) {
    return defaultLangPack;
  }

  // In explorer mode, use absolute path to avoid issues with nested URL paths like /tx/ton/...
  const i18nBasePath = IS_PACKAGED_ELECTRON ? '.' : '..';
  const response = await fetch(`${i18nBasePath}/i18n/${langCode}.json?v=${getLangCacheVersion()}`);

  if (!response.ok) {
    const message = `An error has occured: ${response.status}`;
    throw new Error(message);
  }

  const remote = await response.json();

  if (remote) {
    await cacheApi.save(LANG_CACHE_NAME, langCode, remote);
    return remote;
  }

  return undefined;
}

function getPluralOption(amount: number) {
  const langCode = currentLangCode || DEFAULT_LANG_CODE;
  const optionIndex = PLURAL_RULES[langCode as keyof typeof PLURAL_RULES]
    ? PLURAL_RULES[langCode as keyof typeof PLURAL_RULES](amount)
    : 0;

  return PLURAL_OPTIONS[optionIndex];
}

function getParameterNames(langString: LangString | string | undefined) {
  const templates = typeof langString === 'string'
    ? [langString]
    : PLURAL_VALUE_OPTIONS.map((option) => langString?.[option]).filter(Boolean);
  const names: string[] = [];

  templates.forEach((template) => {
    for (const match of template.matchAll(NAMED_SUBSTITUTION_REGEX)) {
      if (!names.includes(match[1])) names.push(match[1]);
    }
  });

  return names;
}

function processTemplate(template: string, value: any, parameterNames: string[]) {
  const values = Array.isArray(value) ? value : [value];
  const namedValues = Object.fromEntries(parameterNames.map((name, index) => [name, values[index]]));
  const namedResult = template.replace(NAMED_SUBSTITUTION_REGEX, (placeholder, name) => (
    Object.hasOwn(namedValues, name) ? String(namedValues[name] ?? '') : placeholder
  ));

  // Keep accepting positional placeholders while cached language packs from an older version expire.
  if (!SUBSTITUTION_REGEX.test(namedResult)) return namedResult;
  SUBSTITUTION_REGEX.lastIndex = 0;
  let nextValueIndex = 0;
  return namedResult.replace(SUBSTITUTION_REGEX, (_placeholder, position?: string) => {
    const valueIndex = position ? Number(position) - 1 : nextValueIndex++;
    return String(values[valueIndex] ?? '');
  });
}

// Export a function for testing purposes
export function processTemplateJsx(template: string, value: Record<string, TeactNode>): TeactNode[] {
  // Replace all primitive values (strings and numbers)
  let processedTemplate = template;

  Object.entries(value).forEach(([key, val]) => {
    if (typeof val === 'string' || typeof val === 'number') {
      processedTemplate = processedTemplate.replace(
        new RegExp(`%${escapeStringRegexp(key)}%`, 'g'),
        String(val),
      );
    }
  });

  // Check if there are any components
  const hasComponents = Object.values(value).some((val) =>
    typeof val !== 'string' && typeof val !== 'number',
  );

  // If no components, just process through renderText
  if (!hasComponents) {
    return renderText(processedTemplate);
  }

  // If there are components, process in parts
  const parts = processedTemplate.split(/%(\w+)%/);
  const result: TeactNode[] = [];

  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      // Text part
      if (parts[i]) {
        result.push(...renderText(parts[i]));
      }
    } else {
      // Token (between % symbols)
      const tokenValue = value[parts[i]];
      if (tokenValue !== undefined) {
        result.push(tokenValue);
      }
    }
  }

  return result;
}

function processTranslation(
  langString: LangString | string | undefined, key: string, value?: any, format?: 'i', pluralValue?: number,
) {
  const preferredPluralOption = typeof value === 'number' || pluralValue !== undefined
    ? getPluralOption(pluralValue ?? value)
    : 'value';

  const template = typeof langString === 'string'
    ? langString
    : preferredPluralOption === 'value'
      // Support cached older `langString` interface
      ? (typeof langString === 'object' ? (langString as any).value : langString)
      : typeof langString === 'object'
        ? langString?.[preferredPluralOption] || langString.otherValue
        : undefined;

  if (!template || !template.trim() || value === undefined) {
    return template;
  }

  const formattedValue = format === 'i' ? formatNumber(value) : value;
  const result = typeof value === 'object' && !Array.isArray(value)
    ? processTemplateJsx(template, formattedValue)
    : processTemplate(template, formattedValue, getParameterNames(defaultLangPack[key]));
  if (typeof value !== 'object' && typeof result === 'string') {
    const cacheValue = Array.isArray(value) ? JSON.stringify(value) : value;
    cache.set(`${key}_${cacheValue}_${format}${pluralValue ? `_${pluralValue}` : ''}`, result);
  }

  return result;
}

/**
 * Formats a string like "One, Two and Three" or "Foo、Bar 或 Baz".
 * The `items` elements are not translated, you need to translate them separately if you need.
 * When `preferCompact` is true, there will be no spaces around the joiners in languages that don't use spaces normally.
 */
export function formatEnumeration(
  lang: LangFn,
  items: (string | number | boolean | undefined)[],
  joiner: 'and' | 'or',
  preferCompact?: boolean,
): string;
export function formatEnumeration(
  lang: LangFn,
  items: TeactNode[],
  joiner: 'and' | 'or',
  preferCompact?: boolean,
): string | TeactNode[];
export function formatEnumeration(lang: LangFn, items: TeactNode[], joiner: 'and' | 'or', preferCompact?: boolean) {
  const middleJoiner = lang('$joining_comma');
  let lastJoiner = lang(joiner === 'and' ? '$joining_and' : '$joining_or');

  if (preferCompact && lang.code?.startsWith('zh')) {
    lastJoiner = lastJoiner.trim();
  }

  let result: string | TeactNode[];
  let appendResult: (part: TeactNode) => void;

  const isStringResult = items.every((item) => (
    item === undefined
    || typeof item === 'string'
    || typeof item === 'number'
    || typeof item === 'boolean'
  ));

  if (isStringResult) {
    result = '';
    appendResult = (part) => (result as string) += part as string;
  } else {
    result = [];
    appendResult = (part) => (result as TeactNode[]).push(part);
  }

  for (let i = 0; i < items.length; i++) {
    if (i > 0) {
      appendResult(i === items.length - 1 ? lastJoiner : middleJoiner);
    }
    appendResult(items[i] ?? '');
  }

  return result;
}
